import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Waves, FileText, Droplets, LayoutGrid, MapPin, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AIChatWidget from '@/components/AIChatWidget';
import CustomerNavigation from '@/components/CustomerNavigation';
import SEO from '@/components/SEO';
import MapSection from '@/pages/admin/AdminDashboard/MapSection';
import { locationCoordinates, getRiskLevelFromColor } from '@/pages/admin/AdminDashboard/constants';

// Import refactored components and constants
import { CHART_COLORS as COLORS, riskConfig, getLocationCoordinates } from './CustomerDashboard/constants';
import LabReportCard from './CustomerDashboard/LabReportCard';
import AreaSelector from './CustomerDashboard/AreaSelector';

export default function CustomerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [samples, setSamples] = useState([]);
  const [company, setCompany] = useState(null);
  const [barentsWatchFarms, setBarentsWatchFarms] = useState([]);
  const [fishHealthData, setFishHealthData] = useState([]);
  const [selectedCustomerArea, setSelectedCustomerArea] = useState('');
  const [customerAreas, setCustomerAreas] = useState([]);
  const [isReportExpanded, setIsReportExpanded] = useState(false);
  const [allBulkRegistrations, setAllBulkRegistrations] = useState([]); // Store all bulk registrations
  const [mapKey, setMapKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isUsingRealData, setIsUsingRealData] = useState(false);
  const [thresholds, setThresholds] = useState(null);
  
  // Map state (same as admin)
  const [mapLocations, setMapLocations] = useState([]);
  const [allMapLocations, setAllMapLocations] = useState([]);
  const [selectedMapMarker, setSelectedMapMarker] = useState(null);
  const [hoveredMapMarker, setHoveredMapMarker] = useState(null);
  const [selectedMapArea, setSelectedMapArea] = useState('all');
  const [availableMapAreas, setAvailableMapAreas] = useState([]);
  
  // Build map locations from bulk registrations (same logic as admin)
  const buildMapLocations = useCallback((bulks) => {
    if (!bulks?.length) return;
    const locationsMap = new Map();
    let locIndex = 0;
    const sorted = [...bulks].sort((a, b) => new Date(b.date) - new Date(a.date));
    const colorPri = { 'green': 0, 'yellow': 1, 'orange': 2, 'red': 3 };
    
    sorted.forEach(bulk => {
      if (!bulk.algae_data) return;
      const colorOverrides = bulk.color_overrides || {};
      Object.entries(bulk.algae_data).forEach(([key, locData]) => {
        Object.entries(locData).forEach(([location, value]) => {
          if (!value || !value.trim()) return;
          const cleanName = location.includes(' - ') ? location.split(' - ')[1] : location;
          const overrideKey = `${key}_${location}`;
          const cellColor = colorOverrides[overrideKey] || 'green';
          let coords = null;
          const cn = cleanName.toLowerCase().replace(/[.]/g, '');
          for (const [ck, cv] of Object.entries(locationCoordinates)) {
            const kl = ck.toLowerCase().replace(/[.]/g, '');
            if (cn.includes(kl) || kl.includes(cn)) { coords = cv; break; }
          }
          if (!coords) {
            const angle = (locIndex * 137.508) * (Math.PI / 180);
            const r = 0.08 + (locIndex * 0.012);
            coords = { lat: 59.15 + r * Math.cos(angle), lng: 5.85 + r * Math.sin(angle) * 1.5 };
            locIndex++;
          }
          if (!locationsMap.has(cleanName)) {
            locationsMap.set(cleanName, {
              name: cleanName, fullName: location,
              letter: location.split(' - ')[0]?.trim() || cleanName[0],
              lat: coords.lat, lng: coords.lng,
              maxColor: 'green', zooColor: 'green',
              sampleCount: 0, area: bulk.area_name, date: bulk.date,
              temperature: bulk.temperature_data?.[location] || null,
              sampleDepth: bulk.sample_depth || null,
              colorHistory: []
            });
          }
          const loc = locationsMap.get(cleanName);
          if (!loc.colorHistory.find(h => h.date === bulk.date)) {
            let wc = 'green';
            Object.entries(bulk.algae_data).forEach(([ok, od]) => {
              const c = colorOverrides[`${ok}_${location}`] || 'green';
              if (colorPri[c] > colorPri[wc]) wc = c;
            });
            loc.colorHistory.push({ date: bulk.date, color: wc });
            if (loc.colorHistory.length > 5) loc.colorHistory = loc.colorHistory.slice(-5);
          }
          if (bulk.date >= loc.date) {
            loc.sampleCount += 1; loc.date = bulk.date;
            if (bulk.temperature_data?.[location]) loc.temperature = bulk.temperature_data[location];
            if (bulk.zooplankton_data) {
              let wz = 'green';
              Object.entries(bulk.zooplankton_data).forEach(([ok]) => {
                const zc = colorOverrides[`${ok}_${location}`] || 'green';
                if (colorPri[zc] > colorPri[wz]) wz = zc;
              });
              if (colorPri[wz] > colorPri[loc.zooColor]) loc.zooColor = wz;
            }
            if (colorPri[cellColor] > colorPri[loc.maxColor]) loc.maxColor = cellColor;
          }
        });
      });
    });
    const all = Array.from(locationsMap.values());
    setAllMapLocations(all);
    setMapLocations(all);
    setAvailableMapAreas([...new Set(all.map(l => l.area).filter(Boolean))]);
  }, []);

  // Filter map when area changes
  useEffect(() => {
    if (selectedMapArea === 'all') setMapLocations(allMapLocations);
    else setMapLocations(allMapLocations.filter(l => l.area === selectedMapArea));
    setSelectedMapMarker(null);
  }, [selectedMapArea, allMapLocations]);
  
  // Get color based on value and thresholds
  const getColorForValue = useCallback((val) => {
    if (!thresholds) return 'green';
    if (val == null || isNaN(val)) return 'green';
    
    if (val < thresholds.yellow) return 'green';
    if (val < thresholds.orange) return 'yellow';
    if (val < thresholds.red) return 'orange';
    return 'red';
  }, [thresholds]);
  
  // Define fetchData function
  const fetchData = async () => {
    try {
      // Fetch areas and locations from the API first
      const [areasRes, locationsRes] = await Promise.all([
        axios.get(`${API}/areas`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true })
      ]);
      
      const fetchedAreas = areasRes.data || [];
      const fetchedLocations = locationsRes.data || [];
      
      // Build a map of location name -> coordinates
      const locationCoordinatesMap = {};
      fetchedLocations.forEach(loc => {
        const fullName = `${loc.letter} - ${loc.name}`;
        locationCoordinatesMap[fullName] = {
          lat: loc.latitude || 59.0,
          lng: loc.longitude || 5.5,
          area_id: loc.area_id
        };
        // Also map by just the name for flexibility
        locationCoordinatesMap[loc.name] = {
          lat: loc.latitude || 59.0,
          lng: loc.longitude || 5.5,
          area_id: loc.area_id
        };
      });
      
      // Transform areas to include locations count
      const areasWithLocations = fetchedAreas.map(area => ({
        id: area.id,
        name: area.name,
        region: area.region || 'Vestlandet',
        locations: fetchedLocations.filter(loc => loc.area_id === area.id).map(loc => `${loc.letter} - ${loc.name}`)
      }));
      
      if (areasWithLocations.length > 0) {
        setCustomerAreas(areasWithLocations);
        // Select first area by default
        if (!selectedCustomerArea) {
          setSelectedCustomerArea(areasWithLocations[0].id);
        }
      }
      
      // Fetch bulk registrations which contain the actual sample data
      const bulkRes = await axios.get(`${API}/bulk-registrations`, { withCredentials: true });
      
      // Check if we have real bulk registration data
      if (bulkRes.data && bulkRes.data.length > 0) {
        
        // Transform bulk registrations into sample format for display
        const transformedSamples = [];
        let locationIndex = 0;
        
        bulkRes.data.forEach(bulk => {
          // Extract samples from algae_data
          if (bulk.algae_data) {
            Object.entries(bulk.algae_data).forEach(([key, locationData]) => {
              const [group, species] = key.split('_');
              if (species && !species.includes('SUM')) {
                Object.entries(locationData).forEach(([location, value]) => {
                  if (value && value.trim() !== '') {
                    // Parse the value - could be a number or "<5000" etc
                    let cellCount = 0;
                    const cleanValue = value.replace(/[<>]/g, '').replace(/\s/g, '');
                    if (!isNaN(cleanValue)) {
                      cellCount = parseFloat(cleanValue);
                    }
                    
                    // Get coordinates from our location map, or use default
                    const coords = locationCoordinatesMap[location] || getLocationCoordinates(location, locationIndex++);
                    
                    // Find the area for this bulk registration
                    const areaForBulk = areasWithLocations.find(a => a.name === bulk.area_name);
                    
                    transformedSamples.push({
                      id: `${bulk.id}-${key}-${location}`,
                      date: bulk.date,
                      location_name: location,
                      species: group,
                      subspecies: species,
                      cell_count: cellCount,
                      biomass: cellCount / 1000, // For backwards compatibility with charts
                      bulk_id: bulk.id,
                      area_name: bulk.area_name,
                      area_id: areaForBulk?.id || coords?.area_id || null,
                      latitude: coords.lat,
                      longitude: coords.lng
                    });
                  }
                });
              }
            });
          }
        });
        
        // Calculate stats from transformed samples
        const uniqueSpecies = [...new Set(transformedSamples.map(s => s.subspecies))];
        const uniqueLocations = [...new Set(transformedSamples.map(s => s.location_name))];
        
        // Calculate samples in last 30 days - use data's date range, not today
        const allDates = transformedSamples.map(s => new Date(s.date)).filter(d => !isNaN(d));
        const maxDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : new Date();
        const thirtyDaysAgo = new Date(maxDate);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentSamples = transformedSamples.filter(s => new Date(s.date) >= thirtyDaysAgo);
        
        setStats({
          total_samples: bulkRes.data.length, // Number of bulk registrations
          unique_species: uniqueSpecies.length,
          active_locations: uniqueLocations.length,
          samples_last_30_days: [...new Set(recentSamples.map(s => s.date))].length // Unique dates in last 30 days
        });
        setSamples(transformedSamples);
        setIsUsingRealData(true);
        
        // Store all bulk registrations for area-based filtering
        setAllBulkRegistrations(bulkRes.data);
        
        // Build map locations (same logic as admin dashboard)
        buildMapLocations(bulkRes.data);
        
        // Fetch thresholds for color coding
        try {
          const trendsRes = await axios.get(`${API}/analytics/algae-trends`, { withCredentials: true });
          if (trendsRes.data?.thresholds) {
            setThresholds(trendsRes.data.thresholds);
          }
        } catch (e) {
        }
        
        setLoading(false);
        return;
      }
      
      // No real data available, use demo data for demonstration
      loadDemoData();
      
    } catch (error) {
      console.error('API call failed, using demo data:', error);
      loadDemoData();
    }
  };
  
  // Demo data for when there's no real data in the database
  const loadDemoData = async () => {
    // Try to fetch real areas even for demo mode
    try {
      const areasRes = await axios.get(`${API}/areas`, { withCredentials: true });
      if (areasRes.data && areasRes.data.length > 0) {
        const fetchedAreas = areasRes.data.map(area => ({
          id: area.id,
          name: area.name,
          region: area.region || 'Vestlandet',
          locations: area.locations || []
        }));
        setCustomerAreas(fetchedAreas);
        if (!selectedCustomerArea) {
          setSelectedCustomerArea(fetchedAreas[0].id);
        }
      }
    } catch (e) {
    }
    
    const mockSamples = [
          {
            id: 'sample-1',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet A - Toftøy',
            latitude: 59.270767,
            longitude: 5.691433,
            species: 'Kiselalger',
            subspecies: 'Skeletonema',
            biomass: 8.5,
            temperature: 8.2,
            ph: 8.1,
            salinity: 34.5,
            chlorophyll: 2.1,
            created_at: new Date().toISOString(),
            remarks: 'Normale forhold'
          },
          {
            id: 'sample-2',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet B - Føresvik',
            latitude: 59.249767,
            longitude: 5.438233,
            species: 'Kiselalger',
            subspecies: 'Chaetoceros',
            biomass: 12.3,
            temperature: 7.9,
            ph: 8.0,
            salinity: 34.2,
            chlorophyll: 3.2,
            created_at: new Date().toISOString(),
            remarks: 'Moderat aktivitet'
          },
          {
            id: 'sample-3',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet C - Rosshl.',
            latitude: 59.051467,
            longitude: 5.708867,
            species: 'Svepeflagellater',
            subspecies: 'Chryso/Prymn',
            biomass: 5.8,
            temperature: 8.4,
            ph: 8.2,
            salinity: 34.8,
            chlorophyll: 1.9,
            created_at: new Date().toISOString(),
            remarks: 'Lave verdier'
          },
          {
            id: 'sample-4',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet D - Høgsfj.',
            latitude: 58.95365,
            longitude: 5.96495,
            species: 'Kiselalger',
            subspecies: 'Pseudo-Nitzschia',
            biomass: 6.2,
            temperature: 8.0,
            ph: 8.1,
            salinity: 34.6,
            chlorophyll: 2.3,
            created_at: new Date().toISOString(),
            remarks: 'Stabile verdier'
          },
          {
            id: 'sample-5',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet E - Finnøyfj.',
            latitude: 59.139017,
            longitude: 5.86905,
            species: 'Kiselalger',
            subspecies: 'Div',
            biomass: 7.1,
            temperature: 8.3,
            ph: 8.0,
            salinity: 34.4,
            chlorophyll: 2.0,
            created_at: new Date().toISOString(),
            remarks: 'Normal utvikling'
          },
          {
            id: 'sample-6',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet F - Årdalsfj.',
            latitude: 59.133917,
            longitude: 6.040017,
            species: 'Svepeflagellater',
            subspecies: 'Phaeocystis',
            biomass: 9.4,
            temperature: 7.8,
            ph: 8.2,
            salinity: 34.7,
            chlorophyll: 2.8,
            created_at: new Date().toISOString(),
            remarks: 'Moderat aktivitet'
          },
          {
            id: 'sample-7',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet G - Jøsenfj.',
            latitude: 59.2746,
            longitude: 6.244283,
            species: 'Kiselalger',
            subspecies: 'Skeletonema',
            biomass: 5.5,
            temperature: 8.1,
            ph: 8.1,
            salinity: 34.3,
            chlorophyll: 1.8,
            created_at: new Date().toISOString(),
            remarks: 'Lave konsentrasjoner'
          },
          {
            id: 'sample-8',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet H - Erfj.',
            latitude: 59.311383,
            longitude: 6.151933,
            species: 'Fureflagellater',
            subspecies: 'Alexandrium',
            biomass: 13.5,
            temperature: 8.5,
            ph: 8.0,
            salinity: 34.9,
            chlorophyll: 3.5,
            created_at: new Date().toISOString(),
            remarks: 'Høy konsentrasjon - overvåkes'
          },
          {
            id: 'sample-9',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet I - Jelsa',
            latitude: 59.365433,
            longitude: 6.01545,
            species: 'Kiselalger',
            subspecies: 'Chaetoceros',
            biomass: 7.8,
            temperature: 8.2,
            ph: 8.1,
            salinity: 34.5,
            chlorophyll: 2.4,
            created_at: new Date().toISOString(),
            remarks: 'Normal utvikling'
          },
          {
            id: 'sample-10',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet J - Vindafj.',
            latitude: 59.460917,
            longitude: 5.950133,
            species: 'Nåleflagellater',
            subspecies: 'Pseudochatonella',
            biomass: 14.2,
            temperature: 7.7,
            ph: 8.0,
            salinity: 34.1,
            chlorophyll: 3.8,
            created_at: new Date().toISOString(),
            remarks: 'Forhøyet - krever oppfølging'
          },
          {
            id: 'sample-11',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet K - Rott',
            latitude: 58.953383,
            longitude: 5.51905,
            species: 'Kiselalger',
            subspecies: 'Skeletonema',
            biomass: 6.9,
            temperature: 8.3,
            ph: 8.1,
            salinity: 34.6,
            chlorophyll: 2.2,
            created_at: new Date().toISOString(),
            remarks: 'Stabile forhold'
          },
          {
            id: 'sample-12',
            date: new Date().toISOString().split('T')[0],
            area_name: 'P02 Rogaland',
            location_name: 'Lokalitet L - Boknafj.',
            latitude: 59.150617,
            longitude: 5.51545,
            species: 'Kiselalger',
            subspecies: 'Div',
            biomass: 8.1,
            temperature: 8.0,
            ph: 8.2,
            salinity: 34.4,
            chlorophyll: 2.5,
            created_at: new Date().toISOString(),
            remarks: 'Normal konsentrasjon'
          }
        ];
        
        // Set mock stats
        setStats({
          total_samples: mockSamples.length,
          unique_species: [...new Set(mockSamples.map(s => s.species))].length,
          active_locations: mockSamples.length
        });
        setSamples(mockSamples);
      setLoading(false);
  };
  
  useEffect(() => {
    // Force clear browser cache on component mount
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  // Prepare chart data - defined as regular functions (not hooks)
  const prepareTimeSeriesData = () => {
    const sorted = [...samples].sort((a, b) => new Date(a.date) - new Date(b.date));
    return sorted.slice(-10).map(s => ({
      date: new Date(s.date).toLocaleDateString('nb-NO', { month: 'short', day: 'numeric' }),
      biomass: s.biomass || 0,
      temperature: s.temperature || 0
    }));
  };
  
  const prepareSpeciesData = () => {
    const speciesCount = {};
    samples.forEach(s => {
      speciesCount[s.species] = (speciesCount[s.species] || 0) + 1;
    });
    return Object.entries(speciesCount).map(([name, value]) => ({ name, value }));
  };
  
  // Filter samples based on selected area
  const currentArea = customerAreas.find(area => area.id === selectedCustomerArea);
  const areaSamples = currentArea ? 
    samples.filter(sample => sample.area_name === currentArea.name || sample.area_id === currentArea.id) : 
    samples;
  
  // Get the latest report for the selected area
  const latestReport = useMemo(() => {
    if (!allBulkRegistrations.length || !currentArea) return null;
    
    // Filter bulk registrations by selected area
    const areaReports = allBulkRegistrations.filter(
      bulk => bulk.area_name === currentArea.name
    );
    
    if (areaReports.length === 0) return null;
    
    // Sort by date descending and return the latest
    const sorted = [...areaReports].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
    
    return sorted[0];
  }, [allBulkRegistrations, currentArea]);
  
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Memoized unique locations for map markers - aggregate by location and find max color
  const uniqueMapLocations = useMemo(() => {
    if (loading || !latestReport) return [];
    
    const locationsMap = new Map();
    const colorPriority = { 'green': 0, 'yellow': 1, 'orange': 2, 'red': 3 };
    
    // Filter samples to only use the latest date
    const latestDate = latestReport.date;
    const latestSamples = areaSamples.filter(s => s.date === latestDate);
    
    latestSamples.forEach(sample => {
      const locationName = sample.location_name;
      
      if (!locationsMap.has(locationName) && sample.latitude && sample.longitude) {
        locationsMap.set(locationName, {
          ...sample,
          maxColor: 'green',
          maxValue: 0,
          sampleCount: 0
        });
      }
      
      if (locationsMap.has(locationName)) {
        const loc = locationsMap.get(locationName);
        loc.sampleCount += 1;
        
        // Use cell_count for threshold calculation
        const value = sample.cell_count || 0;
        if (value > loc.maxValue) {
          loc.maxValue = value;
        }
        
        // Calculate color based on thresholds
        const cellColor = getColorForValue(value);
        if (colorPriority[cellColor] > colorPriority[loc.maxColor]) {
          loc.maxColor = cellColor;
        }
      }
    });
    
    return Array.from(locationsMap.values());
  }, [areaSamples, loading, latestReport, getColorForValue]);
  
  // Memoized risk calculator - uses maxColor from threshold calculation
  const getRiskLevelForSample = useCallback((sample) => {
    const color = sample?.maxColor || 'green';
    if (color === 'red') return 'high';
    if (color === 'orange' || color === 'yellow') return 'medium';
    return 'low';
  }, []);
  
  // NOW we can have conditional returns - after all hooks are called
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Laster...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white flex">
      <SEO 
        title="Kundeportal"
        description="Se dine algedata, analyseresultater og AI-rapporter i Microsafe kundeportal."
        url="/dashboard"
      />
      <CustomerNavigation companyName={company?.name} />
      <main className="flex-1 min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-8 pt-16 lg:pt-8 animate-fade-in">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-1" data-testid="customer-dashboard-title">
              Dine algedata
            </h1>
            <p className="text-sm text-slate-500">
              Oversikt over analyseresultater og risikonivåer
            </p>
          </div>
        
        {/* Area Selector - Refactored component */}
        <AreaSelector
          selectedArea={selectedCustomerArea}
          onAreaChange={setSelectedCustomerArea}
          areas={customerAreas}
        />
        
        {/* Collapsible Laboratory Summary */}
        <LabReportCard
          isExpanded={isReportExpanded}
          onToggle={() => setIsReportExpanded(!isReportExpanded)}
          latestReport={latestReport}
          currentArea={currentArea}
          areaSamples={areaSamples}
        />
        
        {/* Map - Same as admin dashboard */}
        {mapLocations.length > 0 && (
          <MapSection
            mapLocations={mapLocations}
            selectedMapMarker={selectedMapMarker}
            setSelectedMapMarker={setSelectedMapMarker}
            hoveredMapMarker={hoveredMapMarker}
            setHoveredMapMarker={setHoveredMapMarker}
            selectedMapArea={selectedMapArea}
            availableMapAreas={availableMapAreas}
            onAreaChange={setSelectedMapArea}
          />
        )}
        
        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {prepareTimeSeriesData().length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Trend siste 10 prøver</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={prepareTimeSeriesData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#94a3b8" style={{ fontSize: '11px' }} />
                    <YAxis stroke="#94a3b8" style={{ fontSize: '11px' }} />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#0f172a', 
                        border: 'none', 
                        borderRadius: '12px', 
                        color: '#fff',
                        fontSize: '12px',
                        padding: '8px 12px'
                      }} 
                    />
                    <Line type="monotone" dataKey="biomass" stroke="#0ea5e9" strokeWidth={2} name="Biomasse" dot={false} />
                    <Line type="monotone" dataKey="temperature" stroke="#f97316" strokeWidth={2} name="Temp" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
            </div>
          )}
          
          {/* Species Distribution */}
          {prepareSpeciesData().length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Artsfordeling</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={prepareSpeciesData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => entry.name}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {prepareSpeciesData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        background: '#0f172a', 
                        border: 'none', 
                        borderRadius: '12px', 
                        color: '#fff',
                        fontSize: '12px',
                        padding: '8px 12px'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
            </div>
          )}
        </div>
        </div>
      </main>
      
      {/* AI Chat Widget */}
      <AIChatWidget />
    </div>
  );
}